import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import type { PersonRole } from '@netcon-work/shared'
import { useEffect, useState } from 'react'
import {
  aktivitaetsfeedQuery,
  aufgabenQuery,
  rueckstandQuery,
  sitzungQuery,
  wartendeAbnahmenQuery,
  type Aufgabe,
  type Sitzung,
} from '../lib/queries'
import { MeineKennungProvider } from '../lib/markdown'
import { navigationFuer, siehtAnsicht } from '../lib/navigation'
import { appMarkeSetzen } from '../lib/pwa'
import { connectRealtime, type Verbindungsstatus } from '../lib/realtime'
import { useNachlaufTakt } from '../lib/gelesen'
import { nachrichtenGruppen } from './meintag/Nachrichten'
import { beiSitzungVerloren, trpc } from '../lib/trpc'
import { Anmelden } from '../routes/Anmelden'
import { AufgabeProvider, useOffeneAufgabe } from './AufgabeKontext'
import { CommandPalette } from './CommandPalette'
import { PaletteProvider } from './PaletteContext'
import { SchliesstBeiNavigation, SchubladeProvider, useSchublade } from './Schublade'
import { NetconKopfleiste } from './NetconKopfleiste'
import {
  nachholenFaellig,
  nachruestenMoeglich,
  stillAnmelden,
  stillMoeglich,
} from '../lib/netcon-cloud'
import { ansichtsname, useSeitentitel } from '../lib/seitentitel'
import { SichtBalken } from './SichtBalken'
import { Sidebar, type NavZaehler } from './Sidebar'
import { cn } from '../lib/cn'
import { useNavSchmal } from '../lib/navbreite'
import { useAufgabenDieserWelt } from '../lib/welt'
import { bestandFlicken, FLICKEN_HOECHSTENS } from '../lib/bestand'

/**
 * Rahmen um alle Ansichten: Anmeldeprüfung, Seitenleiste, ⌘K-Palette,
 * WebSocket-Verbindung.
 *
 * Die Anmeldung wird hier abgefangen, nicht in jeder Ansicht: ohne Sitzung
 * erscheint die Maske, mit Sitzung die Anwendung. Die verbindliche Prüfung
 * passiert trotzdem serverseitig in jeder tRPC-Prozedur – das Frontend
 * entscheidet nur, was es anzeigt.
 */
export function AppShell() {
  const pfad = useRouterState({ select: (z) => z.location.pathname })
  const { data: person, isLoading } = useQuery(sitzungQuery)

  /**
   * **Der Rückweg von NetCon.Cloud läuft vor der Sitzungsprüfung** (01.09.2026,
   * [E-292](../../../docs/ENTSCHEIDUNGEN.md#e-292)).
   *
   * Er ist die Stelle, an der die Sitzung **entsteht** – ihn hinter „hast du eine Sitzung?" zu
   * stellen ist ein Kreis: ohne Sitzung erschien die Anmeldemaske, der `code` von Keycloak wurde
   * nie eingelöst, und jeder neue Versuch endete wieder hier.
   *
   * Genau so war es beim ersten Anlauf. Im Protokoll sah man Keycloak brav mit `code`
   * zurückkommen und danach **keinen einzigen** `POST /api/auth/sign-in/netcon-cloud` – der
   * Browser kam nie so weit.
   *
   * Auch **vor** `isLoading`: auf `auth.me` zu warten hat hier keinen Zweck, die Antwort ist
   * bekannt (niemand). Das spart auf dem Rückweg eine Abfrage und ein Flackern.
   */
  if (pfad.startsWith('/auth/netcon')) return <Outlet />

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-[13px] text-muted">
        Lädt …
      </div>
    )
  }

  if (!person) return <AnmeldungOderStillerVersuch />

  return <AngemeldeteAnsicht person={person} />
}

/**
 * **Erst still fragen, dann die Maske zeigen** (01.09.2026,
 * [E-290](../../../docs/ENTSCHEIDUNGEN.md#e-290)).
 *
 * Wer aus dem Portal kommt, ist bei Keycloak schon angemeldet – ihn hier nach einem Klick zu
 * fragen wäre eine Frage, deren Antwort feststeht. `stillMoeglich()` trägt die vier Sicherungen
 * gegen die Endlosschleife; ist auch nur eine nicht erfüllt, erscheint sofort die Maske.
 *
 * **Die Maske bleibt der Normalfall**, nicht die Ausnahme: sie erscheint bei jedem zweiten
 * Aufruf, bei jedem ohne Portal-Sitzung und immer, wenn irgendetwas klemmt.
 */
function AnmeldungOderStillerVersuch() {
  const [versuchtGerade, setVersuchtGerade] = useState(() => stillMoeglich())

  useEffect(() => {
    if (!versuchtGerade) return
    /**
     * Klappt der Absprung nicht binnen zwei Sekunden, zeigen wir die Maske – lieber ein Klick
     * als eine Seite, die „Lädt …" sagt und es nicht tut.
     */
    const notbremse = window.setTimeout(() => setVersuchtGerade(false), 2000)
    void stillAnmelden()
    return () => window.clearTimeout(notbremse)
  }, [versuchtGerade])

  if (versuchtGerade) {
    return (
      <div className="flex min-h-screen items-center justify-center text-[13px] text-muted">
        Lädt …
      </div>
    )
  }
  return <Anmelden />
}

/**
 * **Wer den Seitentitel setzt – und zwar als Einziger** (17.09.2026,
 * [E-365](../../../docs/ENTSCHEIDUNGEN.md#e-365)).
 *
 * `document.title` ist ein einzelner Wert, und zwei Schreiber darauf sind ein Wettlauf: Effekte
 * laufen von innen nach außen, ein Kind überschriebe also zuerst und der Rahmen danach wieder.
 * Deshalb gibt es **eine** Komponente, die alles weiß, was in den Titel gehört, statt eines
 * Hakens, den jede Ansicht selbst setzt.
 *
 * Sie steht **innerhalb** von `AufgabeProvider`: Eine offene Aufgabe ist der wichtigste Fall
 * eines Titels mit Namen – sie ist eine echte Adresse (`?aufgabe=…`, per `pushState`), sie wird
 * in Mails und Kommentaren verschickt, und im Verlauf ist *„Alle Aufgaben"* zum dritten Mal
 * nutzlos, wo *„Rechnungslauf hängt"* stünde.
 *
 * Sie rendert nichts. Eine Komponente ohne Ausgabe ist ungewöhnlich, hier aber das Ehrlichste:
 * Ihre Wirkung liegt im Dokument, nicht im Baum.
 */
function Seitentitel() {
  const pfad = useRouterState({ select: (z) => z.location.pathname })
  const offeneAufgabe = useOffeneAufgabe()
  /**
   * Der Bestand liegt zu diesem Zeitpunkt längst im Zwischenspeicher (`staleTime: Infinity`) –
   * dieser Zugriff holt nichts nach, er hört nur zu.
   */
  const { data: aufgaben } = useQuery(aufgabenQuery)

  const titelDerAufgabe = offeneAufgabe
    ? aufgaben?.find((a: Aufgabe) => a.id === offeneAufgabe)?.title
    : undefined

  /**
   * **Die offene Aufgabe schlägt die Ansicht darunter.** Sie ist das, was der Mensch gerade
   * ansieht – und das, wonach er die Seite im Verlauf wiederfindet.
   *
   * Ist sie noch nicht geladen oder nicht sichtbar, bleibt der Name der Ansicht stehen. Kein
   * Platzhalter: *„Lädt …"* im Verlauf wäre schlimmer als *„Alle Aufgaben"*.
   */
  useSeitentitel(titelDerAufgabe ?? ansichtsname(pfad))
  return null
}

function AngemeldeteAnsicht({ person }: { person: Sitzung }) {
  const [status, setStatus] = useState<Verbindungsstatus>('verbindet')
  const queryClient = useQueryClient()

  /**
   * **Die stille Anmeldung nach dem Rückweg nachholen** (17.09.2026,
   * [Nachtrag zu E-364](../../../docs/ENTSCHEIDUNGEN.md#e-364)).
   *
   * Die einzige Stelle, an der Work **angemeldet** noch einmal zu Keycloak geht – und sie steht
   * deshalb hier und nicht bei der Maske: Wer über den Rückweg hereinkam, hat seine Work-Sitzung
   * schon, und `AnmeldungOderStillerVersuch` läuft für ihn nie.
   *
   * Ohne diesen einen Durchlauf hätte Work **keine Client-Sitzung im Anmeldedienst** – und ein
   * Abmelden im Portal erreichte Work nie, weil Keycloak nur Clients ruft, die in der Sitzung
   * eine eigene haben. Die Kopfleiste verspräche etwas, das nicht einlösbar wäre; deshalb
   * erscheint sie bis dahin auch nicht (`work_anmeldeweg` steht noch auf `lokal`).
   *
   * `nachholenFaellig()` verbraucht den Vermerk beim Lesen – der Effekt kann also nicht
   * schleifen, auch nicht im doppelt laufenden Strict-Modus.
   */
  useEffect(() => {
    if (nachholenFaellig()) void stillAnmelden()
    // Leiste verloren, aber gerade aus dem Portal da: nachrüsten (E-456, `nachruestenMoeglich`).
    else if (person.anmeldeweg !== 'netcon-cloud' && nachruestenMoeglich()) void stillAnmelden()
    // Nur beim Betreten: ein Wechsel des Anmeldewegs danach ist das Ergebnis, kein neuer Anlass.
  }, [])

  /**
   * **Abgelaufene Sitzung heißt Anmeldemaske, nicht Fehlermeldung** (Nils, 04.08.2026).
   *
   * Die Anmeldung wurde bisher nur beim Laden geprüft. Lief das Cookie danach ab, blieb die
   * Oberfläche stehen und jede Aktion scheiterte einzeln – *„Anmeldung erforderlich"* an einer
   * Maske sieht aus, als sei etwas kaputt, und nicht, als sei man abgemeldet. Wer nur liest,
   * merkte es gar nicht.
   *
   * Der Client meldet jetzt jedes 401 (`beiSitzungVerloren`); hier wird daraufhin die Sitzung
   * verworfen. `AppShell` fragt sie neu, bekommt nichts und zeigt die Maske.
   */
  useEffect(
    () =>
      beiSitzungVerloren(() => {
        void queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
      }),
    [queryClient],
  )
  /**
   * **Der Zähler an „Alle Aufgaben" gehört zu einer gebundenen Ansicht** (Nils, 25.08.2026:
   * *„der Alle-Aufgaben-Count-Badge springt beim Workspace-Wechsel noch nicht um"*).
   *
   * Er stand direkt am ungefilterten Gesamtbestand – die Zahl blieb also 3.193, egal wo man
   * stand, während die Liste daneben richtig verengte. Eine Zahl, die nicht zu der Ansicht
   * passt, an der sie klebt, ist schlimmer als keine.
   */
  const { aufgaben } = useAufgabenDieserWelt()

  /**
   * **Der Bestand kommt in zwei Schritten** (20.08.2026,
   * [F-58](../../../../docs/OFFENE-FRAGEN.md)).
   *
   * Nils hat in der Abnahme von Phase 2 gemessen: **2 Sekunden bis zum ersten Bild**, gefordert
   * ist unter einer (Leitplanke 4). Gemessen im Server: `tasks.all` braucht **994 ms** für 3.177
   * Aufgaben und liefert 3 MB – und das bei jedem Seitenaufruf, weil der ganze Bestand im
   * Client liegen soll (Leitplanke 2). Der Leitstand wartet nicht auf seine eigenen Abfragen,
   * die sind in Millisekunden da; er wartet auf diese hier.
   *
   * Von den 3.177 sind rund **113 offen**. Die holt dieser Aufruf zuerst und legt sie in
   * **dieselbe** Zwischenablage; die vollständige Abfrage läuft daneben weiter und ersetzt sie,
   * sobald sie da ist.
   *
   * ## Warum derselbe Schlüssel und keine zweite Abfrage
   *
   * Weil sonst jede Ansicht wissen müsste, aus welcher von zwei Quellen sie liest – und die
   * optimistischen Aktualisierungen (`mutationen.ts`) müssten beide pflegen. Eine Ansicht, die
   * nach einem Klick in den falschen Zwischenspeicher schreibt, zeigt den alten Stand: genau
   * der Fehler, den Leitplanke 1 ausschließen soll. So gibt es weiterhin **einen** Bestand, er
   * ist nur eine Sekunde lang kürzer.
   *
   * `alt ?? offen` und nicht `offen`: kommt die vollständige Antwort zuerst – im LAN gut
   * möglich –, darf die kürzere sie nicht überschreiben.
   */
  useEffect(() => {
    let verworfen = false
    void trpc.tasks.all.query({ nurOffen: true }).then((offen) => {
      if (verworfen) return
      queryClient.setQueryData(aufgabenQuery.queryKey, (alt: Aufgabe[] | undefined) => alt ?? offen)
    })
    return () => {
      verworfen = true
    }
  }, [queryClient])

  useEffect(() => {
    /**
     * Auswertungen gesammelt nachziehen, nicht je Ereignis (AP 2.6).
     *
     * Ein Abgleich schickt nicht ein Ereignis, sondern Dutzende in wenigen
     * Sekunden. Jedes einzeln zu beantworten hieße, die sechs Abfragen des
     * Leitstands dutzendfach neu zu laden – und die Ansicht würde dabei
     * flackern. Also einmal, kurz nachdem der Schwung durch ist.
     */
    let sammler: number | undefined
    const auswertungenNachziehen = () => {
      if (sammler) window.clearTimeout(sammler)
      sammler = window.setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: ['analytics'] })
        /**
         * **Nur die Personen-Abfragen, die am Bestand hängen** (15.10.2026, E-410): Zuweisungs-
         * Vorschlag und Gast-Vorschläge. Hier stand `['people']` – das holte bei jeder
         * Aufgabenänderung auch Personenliste, Erinnerungs- und Arbeitszeit-Einstellungen neu
         * (gemessen: `people.meineErinnerung` 325-mal, `people.all` 253-mal am Tag). Keine davon
         * rechnet aus Aufgaben.
         */
        void queryClient.invalidateQueries({ queryKey: ['people', 'vorschlag'] })
        void queryClient.invalidateQueries({ queryKey: ['people', 'gastVorschlaege'] })
      }, 1_500)
    }

    /**
     * **Der Bestand wird ebenso gesammelt nachgezogen** (21.08.2026).
     *
     * Dieselbe Überlegung wie bei den Auswertungen darüber, nur teurer: `tasks.all` ist
     * die schwerste Abfrage der Anwendung (994 ms, 3 MB, siehe oben). Sie je Ereignis zu
     * beantworten hieß bei einem Abgleich, sie dutzendfach zu holen – und weil
     * `invalidateQueries` laufende Abfragen abbricht (`cancelRefetch`), fing dabei jede
     * Runde die vorige ab, sodass am Ende **eine** durchlief und alle davor Arbeit für
     * nichts waren.
     *
     * **Kurz, nicht lang.** Diese Nachricht ist die Bestätigung der optimistischen
     * Aktionen (`mutationen.ts`); eine Sammelfrist von anderthalb Sekunden hieße
     * anderthalb Sekunden lang unbestätigt. 300 ms fasst einen Klick mit seinem eigenen
     * Echo zusammen und bleibt unbemerkt.
     *
     * ## Nur die genannten Aufgaben (17.09.2026, [E-363](../../../../docs/ENTSCHEIDUNGEN.md#e-363))
     *
     * Nils: *„«Mein Tag» lädt aktuell extrem langsam bei allen."* Jede Nachricht ließ **jeden**
     * verbundenen Browser den ganzen Bestand holen – 3.446 Zeilen, 3 MB. Allein gemessen 400 ms,
     * im Betrieb 3–6 s, weil alle gleichzeitig fragten. Die Nachricht nennt die Aufgabe; geholt
     * wird nur sie (`bestandFlicken`). Der ganze Bestand nur noch, wenn keiner im Speicher liegt
     * oder ein Abgleich mehr als {@link FLICKEN_HOECHSTENS} Aufgaben auf einmal meldet.
     *
     * **Läuft gerade eine eigene Aktion**, wird nicht geflickt, sondern wieder vorgemerkt: die
     * optimistische Änderung (`mutationen.ts`) bricht Abfragen des Bestands ab, damit keine
     * ältere Antwort sie überschreibt – ein `fetch` hier ist keine Abfrage und ließe sich nicht
     * abbrechen.
     */
    let bestandSammler: number | undefined
    const geaendert = new Set<string>()
    const bestandNachziehen = (aufgabeId?: string) => {
      if (aufgabeId) geaendert.add(aufgabeId)
      if (bestandSammler) window.clearTimeout(bestandSammler)
      bestandSammler = window.setTimeout(() => {
        const ids = [...geaendert]
        geaendert.clear()
        const vorhanden = queryClient.getQueryData<Aufgabe[]>(aufgabenQuery.queryKey)
        if (!vorhanden || ids.length === 0 || ids.length > FLICKEN_HOECHSTENS) {
          void queryClient.invalidateQueries({ queryKey: aufgabenQuery.queryKey })
        } else {
          void trpc.tasks.all
            .query({ ids })
            .then((neu) => {
              if (queryClient.isMutating() > 0) {
                for (const id of ids) bestandNachziehen(id)
                return
              }
              queryClient.setQueryData(aufgabenQuery.queryKey, (alt: Aufgabe[] | undefined) =>
                alt ? bestandFlicken(alt, ids, neu) : alt,
              )
            })
            .catch(() => queryClient.invalidateQueries({ queryKey: aufgabenQuery.queryKey }))
        }
        /** Detail, Verlauf, Abnahmen, Feed: alles unter `['tasks']` außer dem Bestand selbst. */
        void queryClient.invalidateQueries({
          queryKey: ['tasks'],
          predicate: (q) => q.queryKey[1] !== 'all',
        })
        /**
         * Pools mit: eine gezogene oder zugewiesene Aufgabe verlässt alle Pools,
         * und sie muss bei allen anderen sofort verschwinden (AP 4.7). Ohne das
         * bliebe sie im Vorrat stehen, bis jemand sie anklickt – und der Klick
         * liefe ins Leere.
         */
        void queryClient.invalidateQueries({ queryKey: ['pools'] })
        /**
         * **Der Eingang mit** (21.08.2026, Nils: *„wenn ich da die Aufgaben bearbeite und
         * dann schließe, bleiben die bis zum Reload in der Liste"*).
         *
         * `eingang.liste` ist eine eigene Abfrage mit einer eigenen Bedingung (`imEingang`:
         * kein Pool, kein Zeitfenster, niemand, der in Frage kommt, kein Work-Bearbeiter) – ein Flicken am Bestand
         * berührt sie nicht, und ein `['tasks']` hier auch nicht. Wer eine Aufgabe im
         * Detailfenster von Hand einordnete, sah sie deshalb weiter im Eingang stehen.
         *
         * Die Zeile verschwindet schon vorher (`ausEingangNehmen` in `mutationen.ts`); das
         * hier ist der Rückweg und der Fall *jemand anderes hat eingeordnet*. Teuer ist es
         * nicht: nachgeladen wird nur, was gerade offen ist – steht der Eingang nicht auf
         * dem Bildschirm, wird die Liste bloß als veraltet vermerkt.
         */
        void queryClient.invalidateQueries({ queryKey: ['eingang'] })
        /**
         * **Der Schirm mit** ([Z-03](../../../../docs/phasen/zusatz-03-auf-dem-schirm.md)).
         *
         * Der wichtigste Fall ist der, den niemand auslöst: **jemand antwortet auf meinen
         * Kommentar**, und die Zeile unter „Ich warte" muss verschwinden. Sie steht in
         * `['schirm', 'meins']`, und weder ein Flicken am Bestand noch `['tasks']` berührt sie –
         * genau die Fehlerklasse aus [E-210](../../../../docs/ENTSCHEIDUNGEN.md), einmal weiter.
         *
         * Vorhergesagt wird hier nichts: ob eine Antwort meine Wartezeile beendet, entscheidet
         * der jüngste Kommentar über zwei Tabellen. Das im Client zu raten hieße, die Regel ein
         * zweites Mal zu schreiben.
         */
        void queryClient.invalidateQueries({ queryKey: ['schirm'] })
      }, 300)
    }

    /**
     * Erste Begrüßung überspringen, jede weitere nachladen.
     *
     * Ein Wiederaufbau heißt: dazwischen sind Nachrichten verloren gegangen. Ohne
     * dieses Nachladen bliebe der Client stumm veraltet – und weil die
     * optimistischen Änderungen (AP 4.7) bewusst *nicht* selbst nachladen,
     * sondern sich auf den Socket verlassen, wäre das die Lücke, in der eine
     * fehlgeschlagene Bestätigung niemandem auffällt.
     */
    let begruesst = false

    /**
     * **Ein abgewiesener Live-Kanal sagt nicht, warum** (17.09.2026).
     *
     * Der Server lehnt den Handschlag ohne Sitzung mit 401 ab (E-225) – der Browser reicht davon
     * nur ein `close` durch, das wie ein Netzfehler aussieht. Der Client versuchte es dann alle
     * 15 Sekunden neu, für immer, und die Sitzung wurde nie geprüft. Also bei jeder Trennung
     * einmal `auth.me` fragen: kommt `null`, erscheint die Anmeldemaske; ist nur der Server kurz
     * weg, schlägt die Abfrage fehl und die Anwendung bleibt stehen, wie sie ist.
     */
    const statusMelden = (neu: Verbindungsstatus) => {
      setStatus(neu)
      if (neu === 'getrennt') void queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
    }

    const trennen = connectRealtime((nachricht) => {
      switch (nachricht.typ) {
        case 'aufgabe.geaendert':
        case 'aufgabe.entfernt':
          bestandNachziehen(nachricht.aufgabeId)
          // Liegezeiten und Hängenbleiber hängen am Aufgabenbestand, nicht am Log.
          auswertungenNachziehen()
          break
        // Der Server verschickt das derzeit nicht – Änderungen kommen als
        // 'aufgabe.geaendert'. Der Zweig bleibt, weil das Log eigene Ereignisse
        // ohne Aufgabenänderung bekommen kann (Kommentare in Phase 6).
        case 'ereignis.neu':
          void queryClient.invalidateQueries({ queryKey: ['events'] })
          auswertungenNachziehen()
          break
        case 'sync.geaendert':
          void queryClient.invalidateQueries({ queryKey: ['system'] })
          /**
           * Abwesenheiten hängen an einem Abgleich, nicht an einer Aufgabe (Z-01) –
           * deshalb kommen sie über diesen Zweig. Sie stehen mit `staleTime:
           * Infinity` im Zwischenspeicher; ohne diese Zeile bliebe ein
           * eingetragener Urlaub bis zum nächsten Neuladen unsichtbar.
           *
           * Und die Aggregate mit: `people.workload` und der Zuweisungs-Vorschlag
           * tragen die Abwesenheit inzwischen mit sich.
           */
          void queryClient.invalidateQueries({ queryKey: ['absences'] })
          auswertungenNachziehen()
          break
        /** Die Ablage hält keinen Bestand im Client – ein Baum je Welt, neu geholt, wenn sich etwas tut (Z-14). */
        case 'dokument.geaendert':
          void queryClient.invalidateQueries({ queryKey: ['dokumente'] })
          break
        case 'willkommen':
          if (begruesst) void queryClient.invalidateQueries()
          begruesst = true
          /**
           * **Läuft hier noch die Oberfläche, die der Server ausliefert?** (18.08.2026)
           *
           * Nils sah eine Ansicht, in der vier Änderungen fehlten – gebaut, ausgeliefert und
           * im Bundle nachweisbar. Sein Browser hatte nur die alte Fassung offen, und von
           * außen ist das von „nicht gebaut" nicht zu unterscheiden.
           *
           * Der Server nennt in der Begrüßung den Dateinamen des Haupt-Bundles; er trägt
           * eine Prüfsumme des Inhalts. Weicht er von dem ab, aus dem diese Seite geladen
           * wurde, ist ein Deploy passiert, seit der Tab offen ist.
           *
           * **Kein Neuladen von selbst.** Wer gerade einen Kommentar tippt, verliert ihn
           * sonst. Ein Hinweis mit Knopf, und die Entscheidung bleibt beim Menschen.
           */
          if (nachricht.version !== 'dev' && nachricht.version !== geladeneFassung()) {
            setNeueFassung(true)
          }
          break
      }
    }, statusMelden)

    return () => {
      if (sammler) window.clearTimeout(sammler)
      if (bestandSammler) window.clearTimeout(bestandSammler)
      trennen()
    }
  }, [queryClient])

  /**
   * **Der Rückstand braucht ein Zeichen in der Seitenleiste** (Nils, 03.08.2026: *„wo sehe
   * ich Rückstand?"*).
   *
   * Er stand die ganze Zeit auf der Einstellungsseite – und genau das war das Problem: eine
   * Liste, die nur sieht, wer sie sucht, beantwortet die Frage nicht, für die es sie gibt.
   * *„Ich habe etwas auf Erledigt gesetzt – ist das angekommen?"*
   *
   * Der Zähler erscheint **nur, wenn etwas offen ist**. Eine dauerhafte `0` wäre die Sorte
   * Anzeige, die man nach zwei Tagen nicht mehr sieht.
   */
  /**
   * **Nur, wer den Punkt sieht, an dem der Zähler hängt** (24.08.2026).
   *
   * Der Rückstand ist eine Diagnose für das Team; für den Fachbereich beantwortet der Server
   * sie mit einem Verbot, und eine Abfrage, von der man weiß, dass sie scheitert, gehört
   * nicht abgeschickt. Seit „KI & Einstellungen" dem Admin gehört, gilt dasselbe für
   * Entwickler und Steuerer aus einem zweiten Grund: die Zahl hätte keinen Ort mehr, an dem
   * sie erscheinen könnte.
   */
  const { data: rueckstand } = useQuery({
    ...rueckstandQuery,
    enabled: siehtAnsicht(person.role, 'einstellungen'),
  })
  const offen = rueckstand?.aufgaben.length ?? 0

  /**
   * **Die aktive Erinnerung an hängende Abnahmen** (Phase 9).
   *
   * Sichtbar waren sie seit dem 05.08. – gemeldet wurden sie nicht, und in zwölf Tagen sind
   * aus 12 wartenden Abnahmen 17 geworden. Eine Ansicht, die man aufsuchen muss, erinnert
   * an nichts.
   *
   * **Keine Mail.** Dafür gäbe es keinen Versandweg auf diesem Server, und eine Mail, die
   * niemand liest, ist dieselbe Ansicht mit mehr Aufwand. Der Zähler steht dort, wo ohnehin
   * jeder hinsieht: an der Navigation, dauerhaft, mit rotem Punkt ab 14 Tagen Wartezeit.
   */
  const [neueFassung, setNeueFassung] = useState(false)
  /** Gleiche Regel wie beim Rückstand: der Zähler sitzt an „Mein Bereich". */
  const { data: abnahmen = [] } = useQuery({
    ...wartendeAbnahmenQuery,
    enabled: siehtAnsicht(person.role, 'bereich'),
  })
  const laengsteWartezeit = abnahmen.reduce((max, a) => Math.max(max, a.wartetTage ?? 0), 0)

  /**
   * **Die Zahl am App-Symbol** (Z-16) – dieselbe Rechnung wie der Reiter „Nachrichten" auf
   * dem Telefon, aus derselben Abfrage (TanStack führt sie einmal). Hier und nicht in „Mein
   * Tag", weil die Zahl auch stimmen soll, wenn jemand die App im Eingang verlassen hat.
   */
  const { data: feed = [] } = useQuery({
    ...aktivitaetsfeedQuery(14),
    enabled: siehtAnsicht(person.role, 'meintag'),
  })
  const takt = useNachlaufTakt(feed)
  const ungelesen = (() => {
    const lage = nachrichtenGruppen(feed, takt)
    return lage.erwaehnt.length + lage.antworten.length
  })()
  useEffect(() => appMarkeSetzen(ungelesen), [ungelesen])

  // Zähler in der Seitenleiste. Pools und Eingang bekommen echte Werte, sobald
  // es sie gibt (Phase 4 bzw. 6) – bis dahin bleiben sie leer statt zu lügen.
  const zaehler: NavZaehler = {
    /**
     * **Ohne Erledigtes** (Nils, 17.09.2026: *„die Erledigten nicht mitzählen … das bringt einem
     * nichts"*). 3.289 sagte nur, wie alt der Bestand ist; die offene Zahl ist die, nach der man
     * in die Liste geht. Zurückgestelltes zählt mit – es ist nicht fertig.
     */
    alle: { anzahl: aufgaben?.filter((a) => a.status !== 'erledigt').length },
    ...(offen > 0 ? { einstellungen: { anzahl: offen } } : {}),
    ...(abnahmen.length > 0
      ? { bereich: { anzahl: abnahmen.length, punkt: laengsteWartezeit >= 14 } }
      : {}),
  }

  return (
    <MeineKennungProvider personId={person.id}>
      <PaletteProvider>
        <NurErreichbaresAnzeigen role={person.role} />
        {/**
         * Das Aufgabendetail steht **hier**, nicht in den Ansichten. Vorher hielt jede
         * Ansicht ihr eigenes – und der Leitstand hatte deshalb keins. Siehe
         * `AufgabeKontext`.
         */}
        <AufgabeProvider>
          <Seitentitel />
          {neueFassung && (
            <div
              role="status"
              className="fixed inset-x-0 unter-leiste z-100 flex items-center justify-center gap-3 bg-ink px-4 py-2 text-[12.5px] text-white"
            >
              <span>
                <strong>Neue Fassung verfügbar.</strong> Diese Seite läuft noch mit dem Stand von
                vorhin.
              </span>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="cursor-pointer rounded-lg bg-white px-2.5 py-1 font-semibold text-ink"
              >
                Neu laden
              </button>
            </div>
          )}
          {/**
           * **Das Raster gilt erst ab 1024 px** ([Z-10](../../../../docs/phasen/zusatz-10-mobil.md)).
           *
           * Darunter gibt es nur eine Spalte: die Seitenleiste liegt dann als Schublade
           * *über* dem Inhalt und ist aus dem Fluss genommen. Vorher stand hier ein festes
           * `grid-cols-[248px_1fr]` – auf einem 390 px breiten Telefon blieben damit 78 px
           * für die Anwendung übrig.
           *
           * Oberhalb der Grenze steht dieselbe Anweisung wie seit Phase 0. Das ist die
           * Bedingung des Pakets: der Arbeitsplatz, an dem täglich gearbeitet wird, ändert
           * sich nicht.
           */}
          {/**
           * **Die Kopfleiste von NetCon.Cloud – nur bei einer Keycloak-Sitzung**
           * (01.09.2026, [E-289](../../../docs/ENTSCHEIDUNGEN.md#e-289)).
           *
           * Sie steht **über** dem Raster und nicht darin: sie gehört dem Portal, nicht Work,
           * und eine Zeile im Raster hätte die Seitenleiste um ihre Höhe verschoben.
           *
           * Wer sich klassisch angemeldet hat, sieht sie nicht — die Leiste verspricht
           * App-Umschalter und gemeinsame Abmeldung, und für eine lokale Sitzung ist dieses
           * Versprechen falsch.
           */}
          {person.anmeldeweg === 'netcon-cloud' && <NetconKopfleiste />}
          <SchubladeProvider>
            <SchliesstBeiNavigation />
            <Raster>
              <Sidebar zaehler={zaehler} status={status} person={person} />
              <Verdunklung />
              <main className="min-w-0 px-4 pb-12 lg:px-8">
                <Outlet />
              </main>
              <CommandPalette />
            </Raster>
          </SchubladeProvider>
          <SichtBalken person={person} />
        </AufgabeProvider>
      </PaletteProvider>
    </MeineKennungProvider>
  )
}

/**
 * Die Fläche hinter der offenen Schublade: sie verdunkelt den Inhalt und schließt bei
 * Berührung. Ab `lg` gibt es keine Schublade und damit nichts zu verdunkeln.
 *
 * `aria-hidden`, weil sie für die Tastatur nichts beiträgt – dort schließt `Esc`. Eine
 * Fläche, die im Tabulator-Lauf auftaucht, wäre eine Station ohne Beschriftung.
 */
function Verdunklung() {
  const { offen, schliessen } = useSchublade()
  if (!offen) return null
  return (
    <div
      aria-hidden
      onClick={schliessen}
      className="fixed inset-x-0 bottom-0 unter-leiste z-40 bg-[rgb(23_22_20/0.38)] lg:hidden"
    />
  )
}

/**
 * **Wer nur zwei Ansichten hat, soll nicht in einer dritten landen** (Phase 9).
 *
 * Ein Mit-Owner, der `/` aufruft – über ein Lesezeichen, die Zurück-Taste oder schlicht die
 * blanke Adresse –, sähe „Mein Tag" mit null Aufgaben. Das ist keine Fehlermeldung, sondern
 * schlimmer: eine Ansicht, die aussieht, als wäre nichts zu tun.
 *
 * Umgeleitet wird auf **seinen Bereich**, weil dort steht, was auf ihn wartet.
 *
 * Kein Ersatz für die Serverprüfung – die Adresszeile ist keine Berechtigung. Er darf hier
 * landen; er soll nur nicht versehentlich landen.
 */
/**
 * `/notizbuch` (21.10.2026, Z-23): das Notizbuch bringt seinen Punkt in der Seitenleiste selbst mit
 * (E-419) und steht deshalb nicht in `navigation.ts` – ohne diesen Eintrag führte der Punkt
 * geradewegs zurück nach „Mein Tag". Wer kein Häkchen hat, sieht dort nur den Hinweis.
 */
export const KEINE_ANSICHT = new Set(['/claude/freigeben', '/notizbuch'])

function NurErreichbaresAnzeigen({ role }: { role: PersonRole }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const navigate = useNavigate()
  useEffect(() => {
    /**
     * Beides **im** Effekt berechnet, nicht davor: eine Liste, die bei jedem Rendern neu
     * entsteht, wäre als Abhängigkeit ein Effekt, der endlos läuft. So hängt er an dem, was
     * sich wirklich ändern kann – Pfad und Rolle.
     */
    const eintraege = navigationFuer(role)
    if (eintraege.some((e) => e.path === pathname)) return
    /**
     * **Seiten, die keine Ansicht sind, bleiben stehen** (16.10.2026, Z-21). Die Freigabe für
     * Claude steht in keiner Seitenleiste und wurde deshalb sofort nach „Mein Tag" umgeleitet –
     * claude.ai wartete auf ein „Erlauben", das niemand zu sehen bekam.
     */
    if (KEINE_ANSICHT.has(pathname)) return
    /** Der erste erreichbare Punkt ist das Ziel – für einen Mit-Owner „Mein Bereich". */
    const ziel: string = eintraege[0]?.path ?? '/'
    void navigate({ to: ziel, replace: true })
  }, [pathname, role, navigate])

  return null
}

/**
 * **Aus welchem Bundle diese Seite geladen wurde.**
 *
 * Vite schreibt genau ein `<script src="/assets/index-<prüfsumme>.js">` in die `index.html`;
 * der Browser führt es aus, und `document.currentScript` ist dabei längst weg. Also wird das
 * Element gesucht – es steht noch im DOM.
 *
 * Findet sich keines (Entwicklungsmodus), gibt es nichts zu vergleichen: der Rückgabewert
 * `dev` passt zu dem, was der Server dann meldet, und der Hinweis erscheint nie.
 */
function geladeneFassung(): string {
  const skript = document.querySelector<HTMLScriptElement>('script[src*="/assets/index-"]')
  return skript?.src.split('/').pop() ?? 'dev'
}

/**
 * **Das Raster aus Leiste und Inhalt** – ab `lg` zwei Spalten; die Leiste ist 248 px breit oder,
 * eingeklappt, 64 px nur mit Symbolen (`lib/navbreite.ts`).
 */
function Raster({ children }: { children: React.ReactNode }) {
  const [schmal] = useNavSchmal()
  return (
    <div
      className={cn(
        'grid min-h-screen',
        schmal ? 'lg:grid-cols-[64px_1fr]' : 'lg:grid-cols-[248px_1fr]',
      )}
    >
      {children}
    </div>
  )
}
